import auraOrb from '@/assets/aura-orb.jpg';
import '@fontsource/orbitron/400.css';
import '@fontsource/orbitron/700.css';
import { Globe, Music, Instagram, ExternalLink } from 'lucide-react';
import { motion } from 'framer-motion';

const links = [
  {
    label: 'Website',
    url: 'https://facelessaura.club/',
    icon: Globe,
  },
  {
    label: 'Spotify Podcast',
    url: 'https://open.spotify.com/show/1VnoF1p9A8NpZlHGFv60B3?si=d9027150845f48c0',
    icon: Music,
  },
  {
    label: 'Instagram',
    url: 'https://www.instagram.com/facelessaura.club/',
    icon: Instagram,
  },
  {
    label: 'TikTok',
    url: 'https://www.tiktok.com/@facelessaura.club',
    icon: ExternalLink,
  },
];

const Links = () => {
  return (
    <div className="min-h-[100dvh] relative overflow-hidden flex items-center justify-center"
      style={{
        background: 'linear-gradient(180deg, #0a0a0f 0%, #0d0d1a 50%, #0a0a0f 100%)',
        fontFamily: "'Outfit', sans-serif",
      }}
    >
      {/* Grid background */}
      <div
        className="absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(0,240,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(0,240,255,0.3) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      {/* Glowing orbs */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] rounded-full opacity-20"
        style={{
          background: 'radial-gradient(circle, rgba(0,240,255,0.35) 0%, rgba(120,0,255,0.15) 40%, transparent 70%)',
          filter: 'blur(40px)',
        }}
      />
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[350px] h-[200px] opacity-10"
        style={{
          background: 'radial-gradient(ellipse, rgba(120,0,255,0.4) 0%, transparent 70%)',
          filter: 'blur(60px)',
        }}
      />

      {/* Content */}
      <div className="relative z-10 w-full max-w-sm mx-auto px-5 py-12 flex flex-col items-center gap-6 safe-area-inset">
        {/* Glowing orb avatar */}
        <motion.div
          className="relative w-24 h-24"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          <div className="absolute -inset-3 rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(120,0,255,0.4) 0%, rgba(0,240,255,0.2) 50%, transparent 70%)',
              filter: 'blur(16px)',
              animation: 'pulse 3s ease-in-out infinite',
            }}
          />
          <img
            src={auraOrb}
            alt="Faceless Aura Club"
            className="relative w-full h-full rounded-full object-cover border-2"
            style={{
              borderColor: 'rgba(0,240,255,0.25)',
              boxShadow: '0 0 30px rgba(120,0,255,0.3), 0 0 60px rgba(0,240,255,0.15)',
            }}
          />
        </motion.div>

        {/* Title */}
        <motion.div
          className="text-center"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          <h1
            className="text-xl font-bold tracking-[0.2em] uppercase mb-1"
            style={{
              fontFamily: "'Orbitron', sans-serif",
              color: '#e0f0ff',
              textShadow: '0 0 20px rgba(0,240,255,0.3)',
            }}
          >
            Faceless Aura
          </h1>
          <p
            className="text-[10px] tracking-[0.35em] uppercase"
            style={{
              fontFamily: "'Orbitron', sans-serif",
              color: 'rgba(0,240,255,0.5)',
            }}
          >
            Club
          </p>
          <p
            className="mt-2 text-xs tracking-wide max-w-[260px] mx-auto leading-relaxed"
            style={{ color: 'rgba(200,215,240,0.55)' }}
          >
            Generating Wealth with Online Businesses Powered by AI.
          </p>
        </motion.div>

        {/* Links */}
        <div className="w-full flex flex-col gap-3">
          {links.map((link, i) => (
            <motion.a
              key={link.label}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative w-full flex items-center gap-4 px-5 py-4 rounded-xl active:scale-[0.97] transition-transform duration-150"
              style={{
                background: 'rgba(255,255,255,0.03)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                border: '1px solid rgba(0,240,255,0.1)',
              }}
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.35 + i * 0.08, duration: 0.5 }}
              whileTap={{ scale: 0.97 }}
            >
              <link.icon
                size={20}
                className="shrink-0"
                style={{ color: 'rgba(0,240,255,0.6)' }}
              />
              <span
                className="text-[13px] tracking-[0.1em] uppercase font-medium"
                style={{
                  fontFamily: "'Orbitron', sans-serif",
                  color: 'rgba(230,240,255,0.85)',
                }}
              >
                {link.label}
              </span>
              <ExternalLink
                size={14}
                className="ml-auto shrink-0 opacity-40"
                style={{ color: 'rgba(0,240,255,0.4)' }}
              />
            </motion.a>
          ))}
        </div>

        {/* Footer */}
        <motion.p
          className="text-[9px] tracking-[0.25em] uppercase mt-3"
          style={{ color: 'rgba(0,240,255,0.2)', fontFamily: "'Orbitron', sans-serif" }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.8 }}
        >
          © 2025 Faceless Aura Club
        </motion.p>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.7; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.08); }
        }
        .safe-area-inset {
          padding-bottom: max(env(safe-area-inset-bottom, 0px), 3rem);
          padding-top: max(env(safe-area-inset-top, 0px), 3rem);
        }
      `}</style>
    </div>
  );
};

export default Links;
